import {Grid, LinearProgress, Typography} from '@mui/material';
import {useQuery} from '@tanstack/react-query';
import {format} from 'date-fns';
import React, {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';

import FilterBar from '../../components/FilterBar/FilterBar';
import PlotLineChart from '../../components/LineChart/LineChart';
import PlotPieChart from '../../components/PieChart/PieChart';
import ToolBar from '../../components/ToolBar/ToolBar';
import {ConfigConstant} from '../../constants/config';
import {getFailedOrdersReport} from '../../service/failedOrders';
import {getDateFromText, getDefaultFilters} from '../../utils/helper';

const getGroupByChannelChartData = (channelData) => {
  const data = [];
  let totalCount = 0;
  if (Array.isArray(channelData) && channelData.length) {
    channelData.forEach((d) => {
      const index = data.push({
        day: format(new Date(d.day), 'dd MMM'),
      });
      if (Array.isArray(d?.channels) && d.channels.length) {
        d.channels.forEach((channel) => {
          if (channel?.channelName === channel?.channelGroupName) {
            data[index - 1][`${channel?.channelName}-direct`] = channel?.count;
            data[index - 1][`${channel?.channelName}-direct-percent`] = `${(
              (channel?.count / d.totalCount) *
              100
            ).toFixed(2)}`;
          } else {
            data[index - 1][`${channel?.channelGroupName}-${channel?.channelName}`] = channel?.count;
            data[index - 1][`${channel?.channelGroupName}-${channel?.channelName}-percent`] = `${(
              (channel?.count / d.totalCount) *
              100
            ).toFixed(2)}`;
          }
          totalCount += channel?.count;
        });
      }
    });
  }
  return {
    data,
    XAxisKey: 'day',
    dataKeys: ConfigConstant.MONGO_CONFIG?.CHANNEL_COLOR_CODES,
    totalCount,
  };
};

const getGroupByBrandChartData = (brandData) => {
  const data = [];
  let top10Count = 0;
  if (Array.isArray(brandData) && brandData.length) {
    brandData[0]?.brands?.forEach((brand, index) => {
      data.push({
        fill: ConfigConstant.MONGO_CONFIG?.PIECHART_COLOR_CODES[index],
        key: brand.partnerBrandName,
        value: brand.count,
      });
      top10Count += brand.count;
    });
    const otherCount = brandData[0]?.totalCount - top10Count || 0;
    if (otherCount > 0) {
      data.push({
        color: ConfigConstant.MONGO_CONFIG?.PIECHART_COLOR_CODES[10],
        key: 'Others',
        value: otherCount,
      });
    }
  }
  return {data, totalCount: brandData[0]?.totalCount};
};

const getGroupByClassificationChartData = (classificationData) => {
  const data = [];
  if (Array.isArray(classificationData) && classificationData.length) {
    classificationData[0]?.classifications.forEach((classification, index) => {
      data.push({
        fill: ConfigConstant.MONGO_CONFIG?.PIECHART_COLOR_CODES[index],
        key: classification.classification,
        value: classification.count,
      });
    });
  }
  return {data, totalCount: classificationData[0]?.totalCount};
};

const getGroupByStoreChartData = (storeData) => {
  const data = [];
  let top10Count = 0;
  if (Array.isArray(storeData) && storeData.length) {
    storeData[0]?.stores?.forEach((store, index) => {
      data.push({
        fill: ConfigConstant.MONGO_CONFIG?.PIECHART_COLOR_CODES[index],
        key: store.partnerPhyStoreName,
        value: store.count,
      });
      top10Count += store.count;
    });
    const otherCount = storeData[0]?.totalCount - top10Count || 0;
    if (otherCount > 0) {
      data.push({
        color: ConfigConstant.MONGO_CONFIG?.PIECHART_COLOR_CODES[10],
        key: 'Others',
        value: otherCount,
      });
    }
  }
  return {data, totalCount: storeData[0]?.totalCount};
};

const getGroupByRejectionReasonChartData = (rejectionReasonData) => {
  const data = [];
  let topCount = 0;
  let totalCount = 0;
  let i = 0;
  if (Array.isArray(rejectionReasonData) && rejectionReasonData.length) {
    Object.keys(rejectionReasonData[0]).forEach((key) => {
      const value = rejectionReasonData[0][key];
      if (key === 'totalCount') {
        totalCount = value;
      } else {
        data.push({
          fill: ConfigConstant.MONGO_CONFIG?.PIECHART_COLOR_CODES[i],
          key,
          value,
        });
        i += 1;
        topCount += value;
      }
    });
  }
  if (totalCount > topCount) {
    data.push({
      fill: ConfigConstant.MONGO_CONFIG?.PIECHART_COLOR_CODES[i],
      key: 'others',
      value: totalCount - topCount,
    });
  }
  return {data, totalCount};
};

const getGroupByToggleLagChartData = (lagData) => {
  const data = {
    productToggle: {
      data: [],
      totalCount: 0,
    },
    storeToggle: {
      data: [],
      totalCount: 0,
    },
  };
  if (Array.isArray(lagData) && lagData.length) {
    let i = 0;
    let j = 0;
    lagData?.forEach((lag) => {
      if (lag.operationType === ConfigConstant.MONGO_CONFIG.OPERATION_TYPES?.PRODUCT_TOGGLE?.code) {
        data.productToggle.data.push({
          fill: ConfigConstant.MONGO_CONFIG?.PIECHART_COLOR_CODES[i],
          key: lag.category,
          value: lag.count,
        });
        i += 1;
        data.productToggle.totalCount += lag.count;
      } else {
        data.storeToggle.data.push({
          fill: ConfigConstant.MONGO_CONFIG?.PIECHART_COLOR_CODES[j],
          key: lag.category,
          value: lag.count,
        });
        j += 1;
        data.storeToggle.totalCount += lag.count;
      }
    });
  }
  return data;
};

const FailedOrderReport = () => {
  const commonFilters = useSelector((store) => store.commonFilters);

  const defaultFilters = getDefaultFilters(window?.location?.search, commonFilters);

  const [filters, setFilters] = useState([
    {
      id: 'Date',
      displayValue: defaultFilters?.date?.displayValue || 'Today',
      value: defaultFilters?.date?.value || getDateFromText('Today'),
      type: 'date',
      selectType: 'single',
      key: 'date',
      isApplied: true,
      isDeleteable: false,
    },
    {
      id: 'City',
      type: 'menu',
      selectType: 'multiple',
      key: 'city',
      displayValue: defaultFilters?.city?.displayValue || [],
      value: defaultFilters?.city?.value || [],
      isApplied: Array.isArray(defaultFilters?.city?.value) && defaultFilters?.city?.value.length > 0 ? true : false,
      isDeleteable: true,
    },
    {
      id: 'Country',
      type: 'menu',
      selectType: 'multiple',
      key: 'countryCode',
      displayValue: defaultFilters?.countryCode?.displayValue || [],
      value: defaultFilters?.countryCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.countryCode?.value) && defaultFilters?.countryCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Channel',
      type: 'menu',
      selectType: 'multiple',
      key: 'channelCode',
      displayValue: defaultFilters?.channelCode?.displayValue || [],
      value: defaultFilters?.channelCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.channelCode?.value) && defaultFilters?.channelCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
  ]);

  const dataQuery = useQuery({
    queryKey: ['data-failedOrdersReport'],
    queryFn: () => getFailedOrdersReport({filters}),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const [channelChartData, setChannelChartData] = useState({});
  const [classificationChartData, setClassificationChartData] = useState({});
  const [brandChartData, setBrandChartData] = useState({});
  const [storeChartData, setStoreChartData] = useState({});
  const [rejectionReasonChartData, setRejectionReasonChartData] = useState({});
  const [channelToggleLagData, setChannelToggleLagData] = useState({});
  const [hubToggleLagData, setHubToggleLagData] = useState({});

  useEffect(() => {
    if (dataQuery.isSuccess) {
      setChannelChartData(getGroupByChannelChartData(dataQuery.data.data?.groupByChannel));
      setClassificationChartData(
        getGroupByClassificationChartData(dataQuery.data.data?.groupByFailedOrderClassification)
      );
      setBrandChartData(getGroupByBrandChartData(dataQuery.data.data?.groupByPartnerBrand));
      setStoreChartData(getGroupByStoreChartData(dataQuery.data.data?.groupByPartnerPhyStore));
      setRejectionReasonChartData(getGroupByRejectionReasonChartData(dataQuery.data.data?.groupByRejectionReason));
      setChannelToggleLagData(getGroupByToggleLagChartData(dataQuery.data.data?.groupByChannelToggleLag));
      setHubToggleLagData(getGroupByToggleLagChartData(dataQuery.data.data?.groupByHubToggleLag));
    }
  }, [dataQuery.data]);
  return (
    <>
      <ToolBar
        headerText={'Failed Orders Reports'}
        showGlobalSearch={false}
        showLegends={false}
        showExportButton={{button: false, tooltip: false}}
      />
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        showApplyActionButton={true}
        showFilterActionButton={true}
        dataQuery={[dataQuery]}
        allowDateRangeSelection={true}
        shouldValidateMultiOpsSelection={true}
      />
      {dataQuery.isFetching || dataQuery.isLoading ? (
        <LinearProgress />
      ) : dataQuery.isError ? (
        <Typography>Failed to fetch reports!</Typography>
      ) : (
        <>
          <PlotLineChart chartData={channelChartData} titleInfo={{text: 'By Channel:', xPercent: '12%'}} />
          <br />
          <PlotPieChart
            chartData={classificationChartData}
            innerRadius={100}
            outerRadius={150}
            titleInfo={{text: 'By Classification:', xPercent: '17%'}}
          />
          <br />
          <PlotPieChart
            chartData={brandChartData}
            innerRadius={100}
            outerRadius={150}
            titleInfo={{text: 'By Brand:', xPercent: '10%'}}
          />
          <br />
          <PlotPieChart
            chartData={storeChartData}
            innerRadius={100}
            outerRadius={150}
            titleInfo={{text: 'By Store:', xPercent: '10%'}}
          />
          <br />
          <PlotPieChart
            chartData={rejectionReasonChartData}
            innerRadius={100}
            outerRadius={150}
            titleInfo={{text: 'By Rejection Reason:', xPercent: '21%'}}
          />
          <br />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <PlotPieChart
                chartData={channelToggleLagData?.productToggle}
                innerRadius={60}
                outerRadius={100}
                titleInfo={{text: 'By Channel Lag - product toggle:', xPercent: '65%'}}
              />
            </Grid>
            <Grid item xs={6}>
              <PlotPieChart
                chartData={channelToggleLagData?.storeToggle}
                innerRadius={60}
                outerRadius={100}
                titleInfo={{text: 'By Channel Lag - store toggle:', xPercent: '60%'}}
              />
            </Grid>
          </Grid>
          <br />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <PlotPieChart
                chartData={hubToggleLagData?.productToggle}
                innerRadius={60}
                outerRadius={100}
                titleInfo={{text: 'By Hub Lag - product toggle:', xPercent: '58%'}}
              />
            </Grid>
            <Grid item xs={6}>
              <PlotPieChart
                chartData={hubToggleLagData?.storeToggle}
                innerRadius={60}
                outerRadius={100}
                titleInfo={{text: 'By Hub Lag - store toggle:', xPercent: '50%'}}
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default FailedOrderReport;
